/**
 * Products API module (customer-facing). SF-SEARCH-002.
 */
import type { ArtworkSpec } from '@dfq/shared/lib/artwork';
import type { FileUploadRule } from '@dfq/shared/lib/fileValidation';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  ProductDetailDTO,
  PublicProductsListEnvelope,
  PublicProductsListParams,
} from '@dfq/shared/types/product';

import { apiClient } from './client';

// ---------------------------------------------------------------------------
// Search types (SF-SEARCH-002)
// ---------------------------------------------------------------------------

/** Query parameters for `GET /api/v1/products` (search variant). SF-SEARCH-002. */
export interface SearchParams {
  q?: string;
  material?: string[];
  vehicleType?: string[];
  priceMin?: number | null;
  priceMax?: number | null;
  sortOrder?: string;
  page?: number;
  perPage?: number;
}

/** Wire-shape of a single product in the search results `items` array. */
export interface ProductSummaryDTO {
  id: number;
  name: string;
  slug: string;
  category_slug: string | null;
  primary_image_url: string | null;
  base_price: string;
  base_price_unit: string;
  is_featured: boolean;
  materials: string[];
}

/** Unwrapped result of `GET /api/v1/products` (search variant). */
export interface PaginatedProductResult {
  items: ProductSummaryDTO[];
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

/**
 * Shape consumed by the storefront configurator's file-upload section.
 * Extends the validation-only {@link FileUploadRule} with per-product
 * configurator fields. Adapted from {@link ArtworkSpec} by
 * {@link artworkSpecToFileUploadRule}.
 */
export interface ProductFileUploadRule extends FileUploadRule {
  /** Numeric product id (mirrors the artwork-spec's productId). */
  id: number;
  /** Product the rule binds to. */
  product_id: number;
  /**
   * Whether the customer MUST upload artwork before adding to cart.
   * `null` is treated as "rule not yet configured / not applicable" and
   * causes the entire upload section to hide.
   */
  is_required: boolean | null;
  /**
   * Maximum print width in inches. Used by the paneling-suggestion
   * banner: when uploaded artwork's `width_px / dpi` exceeds this
   * value, a banner suggests paneling. Null = no width ceiling.
   */
  max_width_inches: number | null;
}

export class ProductsApiError extends Error {
  public readonly code: string | undefined;

  constructor(params: { message: string; code?: string }) {
    super(params.message);
    this.name = 'ProductsApiError';
    this.code = params.code;
  }
}

/**
 * GET /v1/products/{productId}/artwork-spec
 *
 * Returns the merged artwork-spec rule sheet for a product. Accepts a numeric
 * id (required by the platform's `whereNumber` route constraint) or a string
 * for backward compatibility with callers that hold the id as a string.
 * Throws {@link ProductsApiError} on any non-2xx response.
 */
export async function getArtworkSpec(productId: number | string): Promise<ArtworkSpec> {
  const response = await apiClient.get<ApiEnvelope<{ spec: ArtworkSpec }>>(
    `/products/${encodeURIComponent(productId)}/artwork-spec`,
  );
  const envelope = response.data;
  if (envelope.status !== 'success' || !envelope.data) {
    throw new ProductsApiError({
      message: envelope.message,
      code: envelope.error_code ?? envelope.error?.code,
    });
  }
  return envelope.data.spec;
}

/**
 * Adapts an {@link ArtworkSpec} (from `GET /artwork-spec`) into the
 * {@link ProductFileUploadRule} shape the storefront capsule layer expects.
 * Pure function — no I/O.
 */
function artworkSpecToFileUploadRule(
  productId: number,
  spec: ArtworkSpec,
): ProductFileUploadRule {
  const colorMode =
    spec.requiredColorMode === 'cmyk'
      ? 'CMYK'
      : spec.requiredColorMode === 'rgb'
        ? 'RGB'
        : null;

  return {
    id: productId,
    product_id: productId,
    is_required: true,
    accepted_types_json: [...spec.acceptedFormats],
    max_file_size_mb: spec.maxFileSizeBytes / 1_000_000,
    min_dpi: spec.minDpiAtFinalSize ?? null,
    preferred_dpi: spec.preferredDpiAtFinalSize ?? null,
    required_color_mode: colorMode,
    help_text: null,
    special_rules_json: [...(spec.notes ?? [])],
    max_width_inches: spec.finalPrintSizeIn?.width ?? null,
  };
}

/**
 * GET /v1/products/{productId}/artwork-spec (via numeric id)
 *
 * Loads the per-product artwork spec and adapts it into the
 * {@link ProductFileUploadRule} shape consumed by the storefront
 * configurator's file-upload section (SF-FILES-002).
 *
 * The `/file-rules` endpoint does not exist on the platform — only
 * `/artwork-spec` (numeric productId, whereNumber) does. This function
 * calls the existing endpoint and maps the response so callers need
 * no changes.
 *
 * Resolves with `null` when the platform returns 404 — products without
 * a configured spec hide the upload section entirely.
 */
/**
 * Shared cache key for a product's artwork-spec rule, so every consumer on a
 * page reads one fetch instead of issuing its own (SF-CATALOG-121).
 */
export function productFileRulesQueryKey(productId: number): readonly unknown[] {
  return ['product-file-rules', productId] as const;
}

export async function fetchProductFileRules(
  productId: number,
): Promise<ProductFileUploadRule | null> {
  let spec: ArtworkSpec;
  try {
    spec = await getArtworkSpec(productId);
  } catch (err: unknown) {
    // Axios surfaces 404s on err.response.status; treat as no spec configured.
    if (
      typeof err === 'object' &&
      err !== null &&
      'response' in err &&
      typeof (err as { response?: { status?: number } }).response?.status === 'number' &&
      (err as { response: { status: number } }).response.status === 404
    ) {
      return null;
    }
    throw err;
  }

  return artworkSpecToFileUploadRule(productId, spec);
}

/**
 * GET /v1/products/{slug}
 *
 * Fetches the full product detail payload for the storefront configurator.
 * Throws an `ApiError` on any non-2xx response, including:
 *  - `status: 404` when the slug does not match a published product.
 *  - `status: 429` when rate-limited.
 *  - `status: 500` on server error.
 *
 * Callers should catch `ApiError` and read `err.status` to distinguish
 * the 404 (product not found) case from unexpected failures.
 */
export async function getProductBySlug(slug: string): Promise<ProductDetailDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ product: ProductDetailDTO }>>(
      `/products/${encodeURIComponent(slug)}`,
    );
    return unwrap(response.data, response.status).product;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET /v1/products
 *
 * Fetches the public paginated product list. Accepts optional filter params
 * (`category_slug`, `taxonomy_path`, `sort`, `featured`, `page`, `per_page`).
 * `featured: true` sends `?featured=1`, restricting results to admin-curated
 * (`is_featured`) published products.
 *
 * Throws `ApiError` on any non-2xx response.
 */
export async function listPublicProducts(
  params: PublicProductsListParams = {},
): Promise<PublicProductsListEnvelope> {
  // Build a clean query string — omit undefined values so the server
  // sees only the params the caller explicitly set.
  const query = new URLSearchParams();
  if (params.category_slug) query.set('category_slug', params.category_slug);
  if (params.taxonomy_path) query.set('taxonomy_path', params.taxonomy_path);
  if (params.q) query.set('q', params.q);
  if (params.sort) query.set('sort', params.sort);
  if (params.featured) query.set('featured', '1');
  if (params.page !== undefined) query.set('page', String(params.page));
  // The public products endpoint's page-size param is `limit` (1–100, default
  // 24) — NOT `per_page`. Sending `per_page` was silently ignored, so every
  // list capped at 24 regardless of the requested size.
  if (params.per_page !== undefined) query.set('limit', String(params.per_page));

  const qs = query.toString();
  try {
    const response = await apiClient.get<
      ApiEnvelope<{ products: PublicProductsListEnvelope['products'] }> & {
        meta: { pagination: PublicProductsListEnvelope['pagination'] };
      }
    >(`/products${qs ? `?${qs}` : ''}`);

    const { products } = unwrap(response.data, response.status);
    const pagination = response.data.meta.pagination;
    return { products, pagination };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET /api/v1/products (search variant — SF-SEARCH-002).
 *
 * All SearchParams fields are serialised as URL query params.
 * `material[]` and `vehicleType[]` use repeated bracket params.
 * Null/undefined priceMin/priceMax are omitted. HTTP errors propagate
 * as thrown Axios errors so the slice's catch block can extract the envelope.
 */
export async function searchProducts(
  params: Partial<SearchParams> = {},
): Promise<PaginatedProductResult> {
  const qs = new URLSearchParams();

  if (params.q) qs.set('q', params.q);
  if (params.sortOrder) qs.set('sort_order', params.sortOrder);
  if (params.page !== undefined) qs.set('page', String(params.page));
  // The endpoint's page-size param is `limit`; `per_page` is silently ignored,
  // so a typeahead asking for 6 was being served a full page of 24.
  if (params.perPage !== undefined) qs.set('limit', String(params.perPage));
  (params.material ?? []).forEach((m) => qs.append('material[]', m));
  (params.vehicleType ?? []).forEach((v) => qs.append('vehicleType[]', v));
  if (params.priceMin != null) qs.set('priceMin', String(params.priceMin));
  if (params.priceMax != null) qs.set('priceMax', String(params.priceMax));

  const queryString = qs.toString();
  const url = queryString ? `/products?${queryString}` : '/products';
  try {
    const response = await apiClient.get<ApiEnvelope<PaginatedProductResult>>(url);
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

export const productsApi = {
  getArtworkSpec,
  fetchProductFileRules,
  getProductBySlug,
  listPublicProducts,
  searchProducts,
};
